<template>
  <v-card v-if="torneo" class="mb-4">
    <v-card-title>{{ torneo.torneo.nombreTorneo }}</v-card-title>
    <v-card-text>
      <p><strong>Plazas restantes:</strong> {{ plazasRestantes }}</p>
      <p>
        <strong>Fin entrega de listas:</strong>
        {{ formatDate(torneo.torneo.fechaEntregaListas) }}
      </p>
      <p>
        <strong>Fin de inscripción:</strong>
        {{ formatDate(torneo.torneo.fechaFinInscripcion) }}
      </p>
      <p>
        <strong>Pagos:</strong>
        {{ pagosRealizados }}/{{ totalInscripciones }}
      </p>
      <p>
        <strong>Listas entregadas:</strong>
        {{ listasEntregadas }}/{{ listasTotales }}
      </p>
      <p>
        <strong>Listas legales:</strong>
        {{ listasLegales }}/{{ listasTotales }}
      </p>
      <p><strong>Listas de luz:</strong> {{ listasLuz }}</p>
      <p><strong>Listas de oscuridad:</strong> {{ listasOscuridad }}</p>
    </v-card-text>
    <!-- Los mismos botones en todas las gestiones: uno por fila y a lo ancho -->
    <v-card-actions>
      <v-row>
        <v-col cols="12">
          <v-btn variant="tonal" color="secondary" block @click="modificarTorneo()">Modificar torneo</v-btn>
        </v-col>
        <v-col cols="12">
          <v-btn variant="tonal" color="secondary" block @click="modificarBasesTorneo()">Modificar bases</v-btn>
        </v-col>
        <v-col cols="12">
          <v-btn variant="tonal" color="secondary" block @click="handlerMostrarListas">
            {{ mostrarListas ? "Ocultar" : "Mostrar" }} Listas
          </v-btn>
        </v-col>
        <v-col cols="12">
          <v-btn variant="tonal" color="secondary" block @click="handlerMostrarClasificacion">
            {{ mostrarClasificacion ? "Ocultar" : "Mostrar" }} Clasificación
          </v-btn>
        </v-col>
        <v-col cols="12">
          <v-btn variant="tonal" color="secondary" block @click="descargarListasTorneo">Descargar Listas</v-btn>
        </v-col>
        <!-- Acciones propias de la gestión, justo debajo: generar la ronda 1 y el sorteo -->
        <slot name="acciones" />
      </v-row>
    </v-card-actions>
    <v-card-text>
      <CardAddTorneoCircuito :idTorneo="props.torneo?.torneo.idTorneo!" />
    </v-card-text>
  </v-card>

  <!-- modal para modificar el torneo -->
  <ModalModificarTorneo
    :isVisible="showModificarTorneoModal"
    :torneo="torneoMod"
    @close="cerrarModal()"
  />

  <!-- modal para modificar el torneo -->
  <ModalModificarBasesTorneo
    :isVisible="showModificarBasesTorneoModal"
    :torneo="torneoMod"
    @close="cerrarModal()"
  />

  <!-- modal para manejar mostrar listas del torneo -->
  <ModalHandlerMostrarListas
    :isVisible="showModalHandlerMostrarListas"
    :torneo="torneoMod"
    @close="cerrarModal()"
    @confirm="onCambioMostrarListas"
  />

  <!-- modal para manejar mostrar listas del torneo -->
  <ModalHandlerMostrarClasificacion
    :isVisible="showModalHandlerMostrarClasificacion"
    :torneo="torneoMod"
    @close="cerrarModal()"
    @confirm="onCambioMostrarClasificacion"
  />

  <!-- modal torneo modificado con exito -->
  <ModalSuccess
    :isVisible="showSuccessModal"
    message="Torneo modificado con éxito."
    @update:isVisible="showSuccessModal = $event"
  />

  <!-- modal Error al modificar el torneo -->
  <ModalError
    :isVisible="showErrorModal"
    message="No se han podido modificar el torneo. Intentalo de nuevo y si el error persiste contacta con el administrador."
    @update:isVisible="showErrorModal = $event"
  />

  <LoadingLOTR
    :isVisible="isDownloading"
    mensaje="Generando PDF, por favor espera..."
  >
    <template #default>
      <div v-if="isDownloading" style="margin-top: 10px">
        <div style="text-align: center; font-size: 12px; margin-top: 4px">
          {{ progressPDF }}%
        </div>
      </div>
    </template>
  </LoadingLOTR>
</template>

<script setup lang="ts">
import { esUsuarioBye } from "@/utils/bye";
import { computed, ref } from "vue";
import { TorneoGestionInfoMasDTO, Torneo } from "@/interfaces/Torneo";
import { getListasTorneoAsync, getTorneo } from "@/services/TorneosService";
import ModalSuccess from "@/components/Commons/ModalSuccess.vue";
import ModalError from "@/components/Commons/ModalError.vue";
import ModalModificarBasesTorneo from "../ModalModificarBasesTorneo.vue";
import ModalModificarTorneo from "../ModalModificarTorneo.vue";
import ModalHandlerMostrarListas from "../ModalHandlerMostrarListas.vue";
import ModalHandlerMostrarClasificacion from "../ModalHandlerMostrarClasificacion.vue";
import LoadingLOTR from "@/components/Commons/LoadingLOTR.vue";
import { ListaCompletaDTO } from "@/interfaces/Lista";
import jsPDF from "jspdf";
import { ArchivoParaEntregar, entregarArchivos } from "@/utils/entregarArchivos";
import CardAddTorneoCircuito from "../CardAddTorneoCircuito.vue";

const progressPDF = ref<number>(0);

// eslint-disable-next-line no-undef
const props = defineProps<{ torneo: TorneoGestionInfoMasDTO }>();
const showErrorModal = ref<boolean>(false);
const showSuccessModal = ref<boolean>(false);
const showModificarTorneoModal = ref<boolean>(false);
const showModificarBasesTorneoModal = ref<boolean>(false);

const idTorneo = ref<number>(0);
const torneoMod = ref<Torneo>(); // Define adecuadamente el tipo de `torneoMod`
const showModalHandlerMostrarListas = ref<boolean>(false);
const showModalHandlerMostrarClasificacion = ref<boolean>(false);

const mostrarListas = ref(props.torneo?.torneo.mostrarListas);
const mostrarClasificacion = ref(props.torneo?.torneo.mostrarClasificacion);
const isLoading = ref<boolean>(false);
const isDownloading = ref<boolean>(false);

// Las cifras, sin el bye: no es nadie a quien contar ni reclamar
const inscritos = computed(() =>
  (props.torneo?.inscripciones ?? []).filter(i => !esUsuarioBye(i.idUsuario, i.nick))
);

const plazasRestantes = computed(() => {
  if (!props.torneo || inscritos.value.length === null) {
    return "Sin límite";
  }
  if (props.torneo.torneo.limiteParticipantes == null) return "Sin límite";
  return (
    props.torneo.torneo.limiteParticipantes - inscritos.value.length
  );
});

const todasLasListas = computed(() => {
  if (!props.torneo || !inscritos.value) return [];

  return inscritos.value.flatMap(inscripcion => inscripcion.lista);
});

const listasTotales = computed(() => {
  if (!props.torneo) return 0;
  return (
    props.torneo.torneo.listasPorJugador * inscritos.value.length
  );
});

const listasLuz = computed(() => {
  if (!props.torneo) return 0;
  return todasLasListas.value.filter(l => l.bando === "good").length;
});

const listasOscuridad = computed(() => {
  if (!props.torneo) return 0;
  return todasLasListas.value.filter(l => l.bando === "evil").length;
});

const totalInscripciones = computed(() => {
  return props.torneo ? inscritos.value.length : 0;
});

const pagosRealizados = computed(() => {
  if (!props.torneo) return 0;
  return inscritos.value.filter(
    inscripcion => inscripcion.esPago === "SI"
  ).length;
});

const listasLegales = computed(() => {
  if (!props.torneo) return 0;
  return todasLasListas.value.filter(l => l.estadoLista == "OK").length;
});

const listasEntregadas = computed(() => {
  if (!props.torneo) return 0;
  return todasLasListas.value.filter(
    l =>
      l.estadoLista == "ENTREGADA" ||
      l.estadoLista === "ILEGAL" ||
      l.estadoLista === "OK"
  ).length;
});

const formatDate = (date: string | null | undefined) => {
  if (!date) return "N/A";
  return new Date(date).toLocaleDateString();
};

const modificarTorneo = async () => {
  if (props.torneo?.torneo.idTorneo != undefined) {
    idTorneo.value = props.torneo?.torneo.idTorneo;

    try {
      const responseTorneo = await getTorneo(idTorneo.value);
      torneoMod.value = responseTorneo.data;
      showModificarTorneoModal.value = true;
    } catch (error) {
      console.error(error);
      showErrorModal.value = true;
    }
  }
};

const modificarBasesTorneo = async () => {
  if (props.torneo?.torneo.idTorneo != undefined) {
    idTorneo.value = props.torneo?.torneo.idTorneo;

    try {
      const responseTorneo = await getTorneo(idTorneo.value);
      torneoMod.value = responseTorneo.data;
      showModificarBasesTorneoModal.value = true;
    } catch (error) {
      console.error(error);
      showErrorModal.value = true;
    }
  }
};

const cerrarModal = () => {
  showModificarBasesTorneoModal.value = false;
  showModalHandlerMostrarListas.value = false;
  showModalHandlerMostrarClasificacion.value = false;

  showModificarTorneoModal.value = false;
};

const handlerMostrarListas = async () => {
  if (
    props.torneo?.torneo.idTorneo != undefined &&
    props.torneo?.torneo.idTorneo != 0 &&
    props.torneo?.torneo.idTorneo != null
  )
    idTorneo.value = props.torneo?.torneo.idTorneo;

  try {
    const responseTorneo = await getTorneo(idTorneo.value);
    torneoMod.value = responseTorneo.data;
    showModalHandlerMostrarListas.value = true;
  } catch (error) {
    console.error(error);
    showErrorModal.value = true;
  } finally {
    isLoading.value = false;
    showErrorModal.value = false;
  }
};

const onCambioMostrarListas = (nuevoValor: boolean) => {
  mostrarListas.value = nuevoValor;
  showModalHandlerMostrarListas.value = false;
};

const handlerMostrarClasificacion = async () => {
  if (
    props.torneo?.torneo.idTorneo != undefined &&
    props.torneo?.torneo.idTorneo != 0 &&
    props.torneo?.torneo.idTorneo != null
  )
    idTorneo.value = props.torneo?.torneo.idTorneo;

  try {
    const responseTorneo = await getTorneo(idTorneo.value);
    torneoMod.value = responseTorneo.data;
    showModalHandlerMostrarClasificacion.value = true;
  } catch (error) {
    console.error(error);
    showErrorModal.value = true;
  } finally {
    isLoading.value = false;
    showErrorModal.value = false;
  }
};

function chunkArray<T>(arr: T[], size: number): T[][] {
  const result: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size));
  }
  return result;
}

const descargarListasTorneo = async () => {
  if (
    props.torneo?.torneo.idTorneo != undefined &&
    props.torneo?.torneo.idTorneo != 0 &&
    props.torneo?.torneo.idTorneo != null
  )
    idTorneo.value = props.torneo?.torneo.idTorneo;

  try {
    isDownloading.value = true;
    const response = await getListasTorneoAsync(idTorneo.value);
    const listas: ListaCompletaDTO[] = response.data;
    const chunkedListas = chunkArray(listas, 40);
    const total = chunkedListas.reduce((acc, arr) => acc + arr.length, 0);
    let processed = 0;
    const archivos: ArchivoParaEntregar[] = [];
    for (let i = 0; i < chunkedListas.length; i++) {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      let y = 10;
      for (let idx = 0; idx < chunkedListas[i].length; idx++) {
        const lista = chunkedListas[i][idx];
        if (lista.listaData) {
          const imgMatch = lista.listaData.match(
            /^data:image\/(png|jpeg|jpg);base64,/
          );
          if (imgMatch) {
            if (idx !== 0) doc.addPage();

            doc.setFontSize(18);
            doc.text(`${lista.nick}`, pageWidth / 2, 20, { align: "center" });

            const margin = 15;
            const imgProps = doc.getImageProperties(lista.listaData);
            let imgWidth = pageWidth - margin * 2;
            let imgHeight = (imgProps.height * imgWidth) / imgProps.width;
            if (imgHeight > pageHeight - 40 - margin) {
              imgHeight = pageHeight - 40 - margin;
              imgWidth = (imgProps.width * imgHeight) / imgProps.height;
            }
            const x = (pageWidth - imgWidth) / 2;
            const yImg = 30;
            doc.addImage(
              lista.listaData,
              imgMatch[1],
              x,
              yImg,
              imgWidth,
              imgHeight
            );
          } else {
            doc.text(`${lista.nick}`, 10, y);
            y += 10;
            const lines = doc.splitTextToSize(lista.listaData, 180);
            doc.text(lines, 10, y);
            y += lines.length * 7;
            doc.line(10, y, 200, y); // separador
            y += 10;
            if (y > 270 && idx < chunkedListas[i].length - 1) {
              doc.addPage();
              y = 10;
            }
          }
        } else {
          doc.text(`${lista.nick}`, 10, y);
          y += 10;
        }
        processed++;
        progressPDF.value = Math.round((processed / total) * 100);
        await new Promise(resolve => setTimeout(resolve, 0));
      }
      const baseName = props.torneo?.torneo.nombreTorneo
        ? props.torneo?.torneo.nombreTorneo + "_listas"
        : "listas_torneo";
      const fileName =
        chunkedListas.length > 1
          ? `${baseName}_${i + 1}.pdf`
          : `${baseName}.pdf`;
      archivos.push({ blob: doc.output("blob"), nombre: fileName });
    }
    await entregarArchivos(archivos);
  } catch (error) {
    console.error(error);
    showErrorModal.value = true;
  } finally {
    progressPDF.value = 0;
    isDownloading.value = false;
    showErrorModal.value = false;
  }
};

const onCambioMostrarClasificacion = (nuevoValor: boolean) => {
  mostrarClasificacion.value = nuevoValor;
  showModalHandlerMostrarClasificacion.value = false;
};
</script>

<style scoped>
.responsive-text {
  font-size: 16px; /* Tamaño base */
  text-align: center;
}

@media (max-width: 768px) {
  .responsive-text {
    font-size: 14px; /* Tamaño más pequeño para móviles */
  }
}

@media (max-width: 480px) {
  .responsive-text {
    font-size: 12px; /* Ajuste adicional para pantallas muy pequeñas */
  }
}
</style>
